class TokenParseError extends Error {}
class UnexpectedNumOfChunksError extends TokenParseError {}
class TokenChunkNotB64EncodedError extends TokenParseError {}
class TokenClaimsNotAJson extends TokenParseError {}

export class Token {
  readonly claims: Record<string, unknown>

  constructor(public raw: string) {
    this.claims = this.parseClaims(raw)
  }

  parseClaims(raw: string): Record<string, unknown> {
    const chunksEncoded = raw.split('.')
    if (chunksEncoded.length !== 3) {
      throw new UnexpectedNumOfChunksError()
    }

    const claimsEncoded = chunksEncoded[1]

    let claimsSerialized: string
    try {
      claimsSerialized = atob(claimsEncoded)
    } catch (error: unknown) {
      throw new TokenChunkNotB64EncodedError()
    }

    try {
      return JSON.parse(claimsSerialized)
    } catch (error: unknown) {
      throw new TokenClaimsNotAJson()
    }
  }

  expiresAt(): Date {
    const expiresAt = Number(this.claims['exp']) * 1000
    return new Date(expiresAt)
  }

  isExpired(timeNow: Date | null = null) {
    const expiresAtMs = this.expiresAt().getTime()
    const timeNowMs = (timeNow ?? new Date()).getTime()
    const diffMs = expiresAtMs - timeNowMs
    const diffSeconds = diffMs / 1000
    const oneMinute = 60 // seconds => 1 minute
    const minDiffSeconds = oneMinute * 10 // 10 minutes
    return diffSeconds < minDiffSeconds
  }

  static parse(raw: string): Token {
    return new Token(raw)
  }

  // Returns null when raw token cannot be parsed.
  static parseOrNull(raw: string): Token | null {
    try {
      return Token.parse(raw)
    } catch (error: unknown) {
      if (
        error instanceof UnexpectedNumOfChunksError ||
        error instanceof TokenClaimsNotAJson ||
        error instanceof TokenChunkNotB64EncodedError
      ) {
        return null
      }

      throw error
    }
  }
}
